import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  InputLabel,
  List,
  ListItemText,
  ListSubheader,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SavedFilterSelection } from 'redux/ducks/settings';
import SelectItem from 'pages/Report/components/Select/components/SelectItem';
import { AccountData } from 'redux/ducks/reportFilter/selectors';
import { useState } from 'react';
import SearchIcon from '@material-ui/icons/Search';

interface EditSelectionDialogProps {
  selection: SavedFilterSelection;
  onUpdate: (selection: SavedFilterSelection) => void;
  onClose: () => void;
  savedSelections: SavedFilterSelection[];
  availableAccounts: AccountData[];
}

const EditSelectionDialog: React.FunctionComponent<EditSelectionDialogProps> = (props) => {
  const {
    onUpdate,
    onClose,
    selection: { name: nameProp = '', accountIds },
    availableAccounts,
    savedSelections
  } = props;
  const intl = useIntl();
  const [name, setName] = useState<string>(nameProp);
  const [selection, setSelection] = useState<string[]>(accountIds);
  const [search, setSearch] = useState('');

  const classes = useStyles(props);

  const handleUpdate = () => {
    onUpdate({ name, accountIds: selection });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = event;
    setName(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = event;
    setSearch(value);
  };

  const handleSelectChange = (accountId: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    const currentSelectedIds = selection;
    const isSelected = !currentSelectedIds.includes(accountId);
    const nextSelectedIds = isSelected
      ? [...currentSelectedIds, accountId]
      : currentSelectedIds.filter((id) => id !== accountId);
    if (nextSelectedIds.length === 0) {
      return; // must have 1 selected
    } else {
      setSelection(nextSelectedIds);
    }
  };

  const handleSelectAllAccounts = () => {
    setSelection(availableAccounts.map((account) => account.id));
  };

  const handleSelectCurrentAccount = () => {
    const currentAccount = availableAccounts.find((account) => account.isCurrentAccount);
    if (!currentAccount) {
      return;
    }
    setSelection([currentAccount.id]);
  };

  const hasSelectedAll = selection.length === availableAccounts.length;
  const hasSelectedCurrent =
    selection.length === 1 &&
    Boolean(
      availableAccounts.find((account) => account.id === selection[0] && account.isCurrentAccount)
    );

  return (
    <Dialog open onClose={onClose} aria-labelledby='form-dialog-title' maxWidth='sm' fullWidth>
      <DialogTitle id='form-dialog-title'>
        {intl.formatMessage({ id: 'report.filter.editSelection' })}
      </DialogTitle>
      <DialogContent>
        <TextField
          color='primary'
          InputLabelProps={{ shrink: true }}
          autoFocus
          margin='dense'
          label={intl.formatMessage({ id: 'name' })}
          value={name}
          onChange={handleNameChange}
          fullWidth
          helperText={
            name !== nameProp && savedSelections.some((selection) => selection.name === name) ? (
              <Typography color='error' variant='caption'>
                {intl.formatMessage({ id: 'report.filter.savedSelectionExists' })}
              </Typography>
            ) : undefined
          }
        />
        <List>
          <SelectItem
            onClick={handleSelectAllAccounts}
            disabled={hasSelectedAll}
            selected={hasSelectedAll}
            checkbox
          >
            <ListItemText>
              {intl.formatMessage({ id: 'report.filter.selectAllDepartments' })}
            </ListItemText>
          </SelectItem>
          <SelectItem
            onClick={handleSelectCurrentAccount}
            disabled={hasSelectedCurrent}
            selected={hasSelectedCurrent}
            checkbox
          >
            <ListItemText>
              {intl.formatMessage({ id: 'report.filter.selectCurrentDepartment' })}
            </ListItemText>
          </SelectItem>
        </List>
        <Divider />
        <List>
          <ListSubheader disableSticky>
            <InputLabel className={classes.searchHeader}>
              {intl.formatMessage({ id: 'report.filter.orSelectAccountsIndividually' })} (
              {selection.length})
            </InputLabel>
          </ListSubheader>
          <SelectItem disableLookup disableRipple disableHover tabIndex={'-1'}>
            <TextField
              type='search'
              className={classes.searchTextField}
              fullWidth
              placeholder={intl.formatMessage({ id: 'search' })}
              value={search}
              variant='outlined'
              size='small'
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                )
              }}
            />
          </SelectItem>
        </List>
        <List className={classes.scrollableList}>
          {availableAccounts
            .filter((account) => account.displayName?.toLowerCase().includes(search.toLowerCase()))
            .map((account) => (
              <SelectItem
                key={account.id}
                onClick={handleSelectChange(account.id)}
                selected={selection.some((accountId) => accountId === account.id)}
                checkbox
              >
                <ListItemText>{account.displayName}</ListItemText>
              </SelectItem>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={onClose} color='primary'>
          {intl.formatMessage({ id: 'base.cancel' })}
        </Button>
        <Button
          disabled={name.length === 0}
          variant='contained'
          onClick={handleUpdate}
          color='primary'
        >
          {intl.formatMessage({ id: 'base.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  scrollableList: {
    maxHeight: 300,
    overflowY: 'auto'
  },
  searchHeader: {
    fontWeight: 'bold'
  },
  searchTextField: {
    '& .MuiInputBase-input::placeholder': {
      color: theme.palette.primary.main
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.primary.light
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      }
    }
  },
  searchIcon: {
    color: theme.palette.primary.light
  }
}));

export default EditSelectionDialog;
